'use client'

import StreamingChat from '@/components/ChatV2/Chat'
import { FirstCard, ResizableSplitter, SecondCard } from '@/components/Copilot/ResizablePanel'
import { usePanelToggle } from '@/components/Copilot/usePanelToggle'
import { useResizablePanel } from '@/components/Copilot/useResizablePanel'
import { ThemeProvider } from '@/components/Theme/ThemeProvider'
import { AccentColorSync } from '@/components/ThemeSync/AccentColorSync'
import { OutageStatusBanner } from '@/components/ui/OutageStatusBanner'
import { Panel, type BreadcrumbItem, type PanelContent } from '@/components/ui/Panel'
import { Spinner } from '@/components/ui/spinner'
import { WorkflowPanel } from '@/components/Workflow/WorkflowPanel'
import { usePanel } from '@/hooks/usePanel'
import { analytics } from '@/lib/analytics'
import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { CopilotProvider, useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import {
  getInitialMessage,
  setInitialMessage,
} from '@/lib/features/copilotv2/copilotInitialMessageStorage'
import { copilotService } from '@/lib/features/copilotv2/CopilotService'
import { executionService } from '@/lib/features/execution/ExecutionService'
import { GraphicHistoryProvider } from '@/lib/features/graphic/GraphicHistoryContext'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { useGetWorkflowMetadataQuery } from '@/lib/features/workflow/workflowApi'
import {
  handlePanelCopy,
  handlePanelDownload,
  handlePanelExportMarkdown,
  handlePanelShare,
} from '@/lib/panelActions'
import { indexedDbStorage } from '@/lib/storage/indexedDbStorage'
import { AnimatePresence, motion } from 'framer-motion'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AgentType } from 'sprites-types/api/v2/copilot'
import { GraphicProvider, useGraphic } from '../../lib/features/graphic/GraphicContext'
import { GraphicPreviewProvider } from '../../lib/features/graphic/GraphicPreviewContext'
import HtmlFullscreenPanel from '../AgentPlayer/Form/HtmlFullscreenPanel'
import GraphicInspector from '../Graphic/GraphicInspector'
import GraphicPanel from '../Graphic/GraphicPanel'
import { CopilotGraphicActionHandler } from './CopilotGraphicActionHandler'

// Panel open event emitter
type PanelOpenCallback = (content: PanelContent, breadcrumbs: BreadcrumbItem[]) => void

interface PanelOpenEventEmitter {
  subscribe: (callback: PanelOpenCallback) => () => void
}

const PanelOpenEventContext = createContext<PanelOpenEventEmitter | null>(null)

const usePanelOpenEventEmitter = () => {
  const context = useContext(PanelOpenEventContext)
  if (!context)
    throw new Error('usePanelOpenEventEmitter must be used within PanelOpenEventContext')
  return context
}

const commonAnimationProps = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeInOut' as const } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2, ease: 'easeInOut' as const } },
  className: 'absolute inset-0',
}

// Component to handle URL parameter detection and workflow-named message sending
function StartParameterHandler() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const { sendMessage, sessionId, hasHydrated, context, workflowState, loadNodeExecutions } =
    useCopilotContext()

  // Fetch user data for workspace ID
  const { data: userData } = useGetUserQuery()
  const workspaceId = userData?.workspace?.id

  // Get current business profile ID
  const businessProfileId = useCurrentBusinessProfileId()

  // Fetch workflow metadata if we have a workflowId
  const { data: workflowMetadata } = useGetWorkflowMetadataQuery(context.workflowRef || '', {
    skip: !context.workflowRef || workflowState.isBuilding,
  })

  // Track which param combinations we've already handled to prevent infinite loops
  const handledParamsRef = useRef<string>('')

  // Helper function to create a session with initial message and/or agent type
  const createSessionWithParams = useCallback(
    async (
      workspaceId: string,
      message: string | null,
      agentParam: string | null,
      errorContext: string
    ): Promise<void> => {
      try {
        // Create session using the CopilotService
        const data = await copilotService.createSession({
          workspaceId,
          businessProfileId: businessProfileId || undefined,
        })
        const newSessionId = data.session.id

        // Track chat creation event
        let source = 'copilot_page'
        if (message && agentParam) {
          source = 'copilot_page_with_message_and_agent'
        } else if (message) {
          source = 'copilot_page_with_message'
        } else if (agentParam) {
          source = 'copilot_page_with_agent'
        }
        analytics.copilot.userCreatedChat(newSessionId, source)

        // Parse agent type if provided, validate it's a valid AgentType
        const validAgentTypes = Object.values(AgentType)
        const agentType =
          agentParam && validAgentTypes.includes(agentParam as AgentType)
            ? (agentParam as AgentType)
            : undefined

        // Store the message and agent type for the new session
        if (message || agentType) {
          await setInitialMessage(newSessionId, {
            message: message || '',
            agentType,
          })
        }

        console.log(`[StartParameterHandler] Created session ${errorContext}:`, {
          sessionId: newSessionId,
          message: message || undefined,
          agentType,
        })

        // Navigate to the new session
        router.replace(`/copilot/${newSessionId}`)
      } catch (error) {
        console.error(
          `[StartParameterHandler] Error creating copilot session ${errorContext}:`,
          error
        )
      }
    },
    [router, businessProfileId]
  )

  useEffect(() => {
    const sourceParam = searchParams.get('source')
    const executionIdParam = searchParams.get('executionId')
    const tempSessionParam = searchParams.get('tempSession')
    const messageParam = searchParams.get('message')
    const agentParam = searchParams.get('agent')

    // Create a unique key for this param combination
    const paramKey = `${sourceParam}-${executionIdParam}-${tempSessionParam}-${messageParam}-${agentParam}-${sessionId}`

    // Skip if we've already handled these exact params
    if (handledParamsRef.current === paramKey) {
      return
    }

    console.log('[StartParameterHandler] URL params:', {
      source: sourceParam,
      executionId: executionIdParam,
      tempSession: tempSessionParam,
      message: messageParam,
      agent: agentParam,
      sessionId,
      hasHydrated,
      allParams: Object.fromEntries(searchParams.entries()),
    })

    // Handle initial message + agent from URL query parameters
    // This allows external links to start a copilot session with a pre-filled message
    if (messageParam && hasHydrated && !sessionId && workspaceId) {
      console.log('[StartParameterHandler] Detected message param, creating new copilot session', {
        message: messageParam,
        agent: agentParam,
        hasHydrated,
        sessionId,
        workspaceId,
      })

      // Mark as handled before async operations
      handledParamsRef.current = paramKey

      void createSessionWithParams(workspaceId, messageParam, agentParam, 'from message param')
      return
    }

    // Handle agent-only param (start with specific agent, no initial message)
    if (agentParam && !messageParam && hasHydrated && !sessionId && workspaceId) {
      console.log(
        '[StartParameterHandler] Detected agent-only param, creating new session with agent',
        {
          agent: agentParam,
          hasHydrated,
          sessionId,
          workspaceId,
        }
      )

      // Mark as handled before async operations
      handledParamsRef.current = paramKey

      void createSessionWithParams(workspaceId, null, agentParam, 'from agent param')
      return
    }

    // Handle temporary session from landing page signup flow
    if (tempSessionParam && hasHydrated && !sessionId && workspaceId) {
      console.log('StartParameterHandler: Detected tempSession, creating new copilot session', {
        tempSessionParam,
        hasHydrated,
        sessionId,
        workspaceId,
      })

      // Mark as handled before async operations to prevent duplicate calls
      handledParamsRef.current = paramKey

      const handleTempSession = async () => {
        const storedData = await getInitialMessage(tempSessionParam)
        if (storedData?.message) {
          console.log('Creating new copilot session for stored message:', storedData.message)

          try {
            // Create session exactly like Chat component does
            const res = await fetch(
              `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ workspaceId }),
              }
            )

            if (!res.ok) throw new Error('Failed to create session')

            const data = await res.json()
            const newSessionId = data.session.id

            // Handle files if present (convert from base64 back to files and upload)
            let fileLinks
            const storedFiles = await indexedDbStorage.loadFileAttachments(tempSessionParam)
            if (storedFiles) {
              try {
                console.log('Found stored files:', storedFiles.length)

                // Convert base64 back to files and upload them
                const uploadPromises = storedFiles.map(async (fileInfo) => {
                  // Convert base64 back to blob
                  const response = await fetch(fileInfo.data)
                  const blob = await response.blob()
                  const file = new File([blob], fileInfo.filename, { type: fileInfo.mimetype })

                  const formData = new FormData()
                  formData.append('file', file)
                  const uploadRes = await fetch(
                    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/upload-file`,
                    { method: 'POST', body: formData, credentials: 'include' }
                  )
                  if (!uploadRes.ok) {
                    throw new Error(
                      `File upload failed: ${uploadRes.status} ${uploadRes.statusText}`
                    )
                  }
                  const body = await uploadRes.json()
                  if (!body.url) throw new Error('No file URL returned from upload')
                  return {
                    url: body.url,
                    filename: file.name,
                    ext: file.name.split('.').pop() || '',
                  }
                })
                fileLinks = await Promise.all(uploadPromises)
              } catch (error) {
                console.error('Failed to process stored files:', error)
              }
              // Clean up the stored files
              await indexedDbStorage.deleteFileAttachments(tempSessionParam)
            }

            // Store the message for the new session (exactly like Chat component)
            await setInitialMessage(newSessionId, {
              message: storedData.message,
              options: storedData.options,
              fileLinks,
            })

            // Navigate to the new session (exactly like Chat component)
            console.log('StartParameterHandler: Navigating to new session:', newSessionId)
            router.replace(`/copilot/${newSessionId}`)
          } catch (error) {
            console.error('Error creating copilot session:', error)
          }
        }
      }

      void handleTempSession()
    } else if (tempSessionParam) {
      console.log('StartParameterHandler: tempSession found but conditions not met', {
        tempSessionParam,
        hasHydrated,
        sessionId,
        workspaceId,
        conditionMet: tempSessionParam && hasHydrated && !sessionId && workspaceId,
      })
    } else if (
      (sourceParam === 'template' || sourceParam === 'workflow') &&
      sessionId &&
      hasHydrated &&
      workflowMetadata
    ) {
      console.log('StartParameterHandler: Detected source=template, sending workflow message')

      // Mark as handled before async operations
      handledParamsRef.current = paramKey

      // Send message with workflow name
      const message = `I just loaded the "${workflowMetadata.name}" automation workflow here- what can we do with it?`
      sendMessage(message).catch((error) => {
        console.error('Failed to send message:', {
          error,
          message,
          workflowName: workflowMetadata.name,
          sessionId,
        })
      })

      // Clean up URL by removing the source parameter
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.delete('source')
      router.replace(currentUrl.pathname + currentUrl.search, { scroll: false })
    } else if (
      sourceParam === 'execution' &&
      workflowMetadata &&
      executionIdParam &&
      sessionId &&
      hasHydrated
    ) {
      console.log('StartParameterHandler: Detected source=execution, loading execution data')

      // Mark as handled before async operations
      handledParamsRef.current = paramKey

      // Get execution context from IndexedDB (async)
      executionService
        .getExecutionContext(sessionId)
        .then((executionContext) => {
          if (executionContext) {
            console.log('StartParameterHandler: Found execution context', {
              executionId: executionContext.executionId,
              nodeExecutionsCount: Object.keys(executionContext.nodeExecutions).length,
            })

            // Load the node executions into the copilot context
            if (
              executionContext.nodeExecutions &&
              Object.keys(executionContext.nodeExecutions).length > 0
            ) {
              console.log('StartParameterHandler: Loading node executions into context', {
                count: Object.keys(executionContext.nodeExecutions).length,
              })
              loadNodeExecutions(executionContext.nodeExecutions)
            }

            // Send message about the execution
            const message = `Give me a summary.`
            sendMessage(message).catch((error) => {
              console.error('Failed to send message:', {
                error,
                message,
                workflowName: workflowMetadata.name,
                sessionId,
              })
            })

            // Clean up URL by removing the source and executionId parameters
            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.delete('source')
            currentUrl.searchParams.delete('executionId')
            router.replace(currentUrl.pathname + currentUrl.search, { scroll: false })
          } else {
            console.error(
              'StartParameterHandler: No execution context found for session',
              sessionId
            )
          }
        })
        .catch((error) => {
          console.error('StartParameterHandler: Failed to load execution context:', error)
        })
    }
  }, [
    searchParams,
    sendMessage,
    sessionId,
    hasHydrated,
    router,
    workflowMetadata,
    workspaceId,
    loadNodeExecutions,
    createSessionWithParams,
  ])

  return null // This component doesn't render anything
}

// Chat Panel
function ChatContent() {
  const { graphic } = useGraphic()

  const renderContent = () => {
    if (graphic) {
      return (
        <motion.div key="graphic-preview" {...commonAnimationProps}>
          <GraphicInspector />
        </motion.div>
      )
    }

    return (
      <motion.div key="workflow-panel" {...commonAnimationProps}>
        <Suspense fallback={<Spinner variant="simple" size="md" />}>
          <StreamingChat />
        </Suspense>
      </motion.div>
    )
  }

  return (
    <div className="relative h-full w-full">
      <AnimatePresence mode="sync">{renderContent()}</AnimatePresence>
    </div>
  )
}

function SecondContent() {
  const { graphic } = useGraphic()
  const copilotContext = useCopilotContext()
  const { context, workflowState, updateWorkflowRef, loadNodeExecutions } = copilotContext
  const [showPanel, setShowPanel] = useState(false)
  const router = useRouter()

  // Set up panel functionality
  const {
    currentContent,
    breadcrumbs,
    pushContent,
    goBack,
    canGoBack,
    navigateToIndex,
    clearHistory,
  } = usePanel({
    enableBrowserHistory: true,
    historyPrefix: 'workflow-panel',
  })

  const handleSecondContentPanelOpen = useCallback(
    (content: PanelContent, breadcrumbs: BreadcrumbItem[]) => {
      // Replace panel instead of stacking when opening execution results over execution results
      if (
        currentContent?.type === 'workflow-execution-results' &&
        content.type === 'workflow-execution-results'
      ) {
        clearHistory()
      }
      pushContent(content, breadcrumbs)
      setShowPanel(true)
    },
    [pushContent, currentContent, clearHistory]
  )

  const secondContentHandlerRef = useRef(handleSecondContentPanelOpen)
  useEffect(() => {
    secondContentHandlerRef.current = handleSecondContentPanelOpen
  }, [handleSecondContentPanelOpen])

  const eventEmitter = usePanelOpenEventEmitter()
  useEffect(() => {
    return eventEmitter.subscribe((content, breadcrumbs) =>
      secondContentHandlerRef.current(content, breadcrumbs)
    )
  }, [eventEmitter])

  const handlePanelBack = useCallback(() => {
    if (canGoBack) {
      goBack()
    } else {
      setShowPanel(false)
      // Clear panel history to prevent breadcrumb accumulation
      clearHistory()
      // Remove panel hash from URL when closing
      const url = window.location.pathname + window.location.search
      window.history.pushState(null, '', url)
    }
  }, [canGoBack, goBack, clearHistory])

  const handlePanelNavigate = useCallback(
    (index: number) => {
      navigateToIndex(index)
    },
    [navigateToIndex]
  )

  const handlePanelAction = useCallback(
    async (actionId: string) => {
      if (!currentContent) return

      const actionContext = {
        content: currentContent,
        executionId: context.workflowExecutionId,
        getNodeExecution: (execId: string) => workflowState.nodeExecutions?.[execId],
      }

      try {
        switch (actionId) {
          case 'download':
            await handlePanelDownload(actionContext)
            break
          case 'share':
            await handlePanelShare(actionContext)
            break
          case 'copy':
            await handlePanelCopy(actionContext)
            break
          case 'export-markdown':
            await handlePanelExportMarkdown(actionContext)
            break
          case 'fork':
            // Navigate to /results/<executionId> for workflow-execution-results
            if (currentContent.type === 'workflow-execution-results') {
              router.push(`/results/${currentContent.executionId}`)
            }
            break
          case 'load-to-context':
            // Load execution data into context for workflow-execution-results
            if (currentContent.type === 'workflow-execution-results') {
              try {
                const executionData = await executionService.getExecutionData(
                  currentContent.executionId
                )
                // Load workflow ref and node executions into context
                if (executionData.executionData.workflowRef) {
                  updateWorkflowRef(executionData.executionData.workflowRef)
                  loadNodeExecutions(executionData.nodeExecutions)
                  console.log('[CopilotPage] Loaded execution data into context:', {
                    executionId: currentContent.executionId,
                    workflowRef: executionData.executionData.workflowRef,
                    nodeExecutionCount: Object.keys(executionData.nodeExecutions).length,
                  })
                  // Close the panel after successfully loading to context
                  setShowPanel(false)
                  clearHistory()
                }
              } catch (error) {
                console.error('[CopilotPage] Failed to load execution to context:', error)
                alert(
                  'Failed to load workflow execution data. Please try again or check the console for details.'
                )
              }
            }
            break
          default:
            console.log('Unknown panel action:', actionId)
        }
      } catch (error) {
        console.error('Panel action failed:', error)
      }
    },
    [
      currentContent,
      context.workflowExecutionId,
      workflowState.nodeExecutions,
      router,
      updateWorkflowRef,
      loadNodeExecutions,
      setShowPanel,
      clearHistory,
    ]
  )

  // Close panel when navigating away from panel URLs
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash
      if (!hash.includes('workflow-panel') && showPanel) {
        setShowPanel(false)
        clearHistory()
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [showPanel, clearHistory])

  // Close panel when currentContent becomes null (history cleared)
  useEffect(() => {
    if (!currentContent && showPanel) {
      setShowPanel(false)
    }
  }, [currentContent, showPanel])

  const renderContent = () => {
    if (context.htmlFullscreen) {
      return (
        <motion.div key="html-fullscreen" {...commonAnimationProps}>
          <HtmlFullscreenPanel />
        </motion.div>
      )
    }

    if (graphic) {
      return (
        <motion.div key="graphic-preview" {...commonAnimationProps}>
          <GraphicPanel />
        </motion.div>
      )
    }

    return (
      <motion.div key="workflow-panel" {...commonAnimationProps}>
        <WorkflowPanel onOpenPanel={handleSecondContentPanelOpen} />
      </motion.div>
    )
  }

  return (
    <div className="relative h-full w-full">
      <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>

      {/* Panel Overlay with Animation */}
      <AnimatePresence>
        {showPanel && currentContent && (
          <motion.div
            className="absolute inset-0 z-30"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              duration: 0.4,
            }}
          >
            <Panel
              content={currentContent}
              breadcrumbs={breadcrumbs}
              onBack={handlePanelBack}
              onNavigateTo={handlePanelNavigate}
              onAction={handlePanelAction}
              onOpenPanel={handleSecondContentPanelOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Main content component that uses the context
function StreamingEditPageContent({
  initialCookieString,
  eventEmitter,
}: {
  initialCookieString?: string
  conversationId?: string
  eventEmitter: PanelOpenEventEmitter
}) {
  const {
    firstPanelWidth,
    oneCardMode,
    containerRef,
    handleSplitterMouseDown,
    handleSplitterDoubleClick,
  } = useResizablePanel(initialCookieString)

  const { showSecondPanel, handleTogglePanel } = usePanelToggle()

  const firstPanelStyle = {
    width: oneCardMode ? '100%' : `${firstPanelWidth}%`,
  }

  return (
    <PanelOpenEventContext.Provider value={eventEmitter}>
      <OutageStatusBanner />
      <div className="flex h-full w-full items-center justify-center">
        <Suspense fallback={null}>
          <StartParameterHandler />
        </Suspense>
        <div ref={containerRef} className="flex h-full w-full flex-row p-2">
          {/* First card - resizable width */}
          <div className="h-full" style={firstPanelStyle}>
            <FirstCard
              shouldShowOneCard={oneCardMode}
              showSecondPanel={showSecondPanel}
              onTogglePanel={handleTogglePanel}
              firstContent={<ChatContent />}
              secondContent={<SecondContent />}
              firstName="Chat"
              secondName="Workflow"
            />
          </div>

          {/* Resizable splitter - only show when not in chat-only mode */}
          {!oneCardMode && (
            <ResizableSplitter
              onMouseDown={handleSplitterMouseDown}
              onDoubleClick={handleSplitterDoubleClick}
            />
          )}

          {/* Second card - only show when not in chat-only mode */}
          {!oneCardMode && (
            <div className="h-full flex-1">
              <SecondCard>
                <SecondContent />
              </SecondCard>
            </div>
          )}
        </div>
      </div>
    </PanelOpenEventContext.Provider>
  )
}

interface StreamingEditPageProps {
  initialCookieString?: string
  conversationId?: string
}

export default function StreamingEditPage({
  initialCookieString,
  conversationId,
}: StreamingEditPageProps) {
  const router = useRouter()
  const isDebug = useSearchParams().get('debug') === 'true'

  const subscribersRef = useRef<Set<PanelOpenCallback>>(new Set())

  const handleSessionIdChange = useCallback(
    (newSessionId: string) => {
      console.log('StreamingEditPage: Session ID changed to', newSessionId, 'isDebug', isDebug)
      setTimeout(() => {
        router.replace(`/copilot/${newSessionId}${isDebug ? '?debug=true' : ''}`, { scroll: false })
      }, 100)
    },
    [router]
  )

  const eventEmitter = useMemo<PanelOpenEventEmitter>(
    () => ({
      subscribe: (callback) => {
        subscribersRef.current.add(callback)
        return () => subscribersRef.current.delete(callback)
      },
    }),
    []
  )

  const handleOpenPanel = useCallback((content: PanelContent, breadcrumbs: BreadcrumbItem[]) => {
    subscribersRef.current.forEach((callback) => callback(content, breadcrumbs))
  }, [])

  return (
    <ThemeProvider>
      <GraphicProvider>
        <GraphicPreviewProvider>
          <CopilotProvider
            conversationId={conversationId}
            onSessionIdChange={handleSessionIdChange}
            onOpenPanel={handleOpenPanel}
          >
            <AccentColorSync />
            <GraphicHistoryProvider>
              <CopilotGraphicActionHandler>
                <StreamingEditPageContent
                  initialCookieString={initialCookieString}
                  conversationId={conversationId}
                  eventEmitter={eventEmitter}
                />
              </CopilotGraphicActionHandler>
            </GraphicHistoryProvider>
          </CopilotProvider>
        </GraphicPreviewProvider>
      </GraphicProvider>
    </ThemeProvider>
  )
}
